import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Page } from "@boundary/layouts/pageLayout";
import { SpinnerContainer } from "@boundary/components/spinner";
import { SlideToConfirm } from "@boundary/components/slideToConfirm";
import { OrderContent } from "@boundary/widgets/orderContent";
import { OrderService } from "@domain/services/order.service";
import type { OrderHistoryItem } from "@domain/dtos/models/order.models";
import { OrderHeader, OrderStatusIcon } from "@boundary/components/orderHeader";
import { HandleableError } from "@shared/utils/errors/handleableError";
import { hasAskedWriteAccess, requestWriteAccess } from "@shared/integrations/telegram/init";
import styles from "./order.module.scss";

const NEAR_MACHINE_HINT = "Открывайте, только когда стоите возле аппарата";
// Пауза перед запросом разрешения: экран заказа должен успеть отрисоваться,
// иначе окно Telegram выскакивает поверх пустой страницы.
const WRITE_ACCESS_DELAY_MS = 1500;

export function OrderPage() {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<OrderHistoryItem | null>(null);
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    const data = await OrderService.getOrderById(id);
    setOrder(data);
  }, [id]);

  useEffect(() => {
    void fetchOrder();
  }, [fetchOrder]);

  // Заказ оформлен — момент, когда уместно попросить разрешение писать: пользователю
  // очевидно, зачем это нужно. Приложение открывается по прямой ссылке, минуя диалог
  // с ботом, поэтому без разрешения сообщение о готовности заказа не дойдёт.
  useEffect(() => {
    if (!order || hasAskedWriteAccess()) {
      return;
    }

    const timer = setTimeout(() => {
      void requestWriteAccess();
    }, WRITE_ACCESS_DELAY_MS);

    return () => clearTimeout(timer);
  }, [order]);

  const canOpenCell = order?.status === "ready";

  const handleOpenCell = useCallback(async () => {
    if (!id || opening) return;

    setOpening(true);
    setOpenError(null);
    try {
      await OrderService.openCell(id);
      // Статус заказа меняется на бэке; перечитываем его, не дожидаясь WebSocket.
      await fetchOrder();
    } catch (error) {
      const reason = error instanceof HandleableError ? error.description : undefined;
      setOpenError(reason || "Не удалось открыть ячейку. Попробуйте ещё раз.");
    } finally {
      setOpening(false);
    }
  }, [id, opening, fetchOrder]);

  return (
    <Page className={styles.detail}>
      <Page.SafeArea />
      <Page.DisplayZone>
        <Page.DisplayZone.Header
          className={styles.header}
          back
          center={<OrderHeader order={order} orderId={id} />}
          right={<OrderStatusIcon order={order} />}
        />
        <Page.DisplayZone.Body className={styles.content}>
          {order ? <OrderContent item={order} /> : <SpinnerContainer />}
        </Page.DisplayZone.Body>
        <Page.DisplayZone.Sticky className={styles.buttonPad}>
          <SlideToConfirm
            label="Открыть ячейку"
            hint={NEAR_MACHINE_HINT}
            error={openError || undefined}
            disabled={!canOpenCell}
            loading={opening}
            analyticsName="Открыть ячейку"
            onConfirm={handleOpenCell}
          />
        </Page.DisplayZone.Sticky>
      </Page.DisplayZone>
    </Page>
  );
}
